import type { ReactNode } from 'react';

/**
 * The paid report's shared furniture, copied from production's `report.tsx`.
 *
 * Production numbers its report sections — `Section 01` through `Section 06` —
 * with the number set apart from the title above a rule. V2 rendered plain
 * headings, which loses the sense that the report is a document you work
 * through in order.
 */
export function SectionHeader({ label, title }: { label: string; title: string }) {
  return (
    <div
      className="flex items-baseline gap-4 border-b border-border pb-2"
      data-testid="report-section-header"
      data-label={label}
    >
      <span className="shrink-0 text-xs font-bold uppercase tracking-widest text-muted-foreground">
        {label}
      </span>
      <h2 className="text-xl font-bold">{title}</h2>
    </div>
  );
}

/**
 * Production's three block treatments: plain, `accent` for the two blocks it
 * sets apart on a tinted ground, and `highlight` for the one it puts in the
 * brand colour.
 */
export type BlockVariant = 'default' | 'accent' | 'highlight';

const BLOCK_STYLES: Record<BlockVariant, string> = {
  default: 'border-border bg-card',
  accent: 'border-border bg-secondary/40',
  highlight: 'border-primary/20 bg-primary/5',
};

export function ReportBlock({
  title,
  children,
  variant = 'default',
}: {
  title: string;
  children: ReactNode;
  variant?: BlockVariant;
}) {
  return (
    <div
      className={`rounded-xl border p-6 ${BLOCK_STYLES[variant]}`}
      data-testid="report-block"
      data-title={title}
    >
      <p className="mb-3 text-xs font-bold uppercase tracking-wider text-muted-foreground">
        {title}
      </p>
      <p className="text-sm leading-relaxed">{children}</p>
    </div>
  );
}

/** A labelled column of items, as the Stop/Start and Reflection panels use. */
export function LabelledPanel({
  label,
  labelClass = 'text-muted-foreground',
  children,
  testId,
}: {
  label: string;
  labelClass?: string;
  children: ReactNode;
  testId?: string;
}) {
  return (
    <div
      className="flex flex-col gap-3 rounded-xl border border-border bg-card p-6"
      {...(testId ? { 'data-testid': testId } : {})}
    >
      <h3 className={`text-xs font-bold uppercase tracking-wider ${labelClass}`}>{label}</h3>
      {children}
    </div>
  );
}

/** The small uppercase label production uses inside its day and week cards. */
export function FieldLabel({ children }: { children: ReactNode }) {
  return (
    <p className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
      {children}
    </p>
  );
}

/** A prompt: italic, indented behind a rule. Production's journal styling. */
export function PromptList({ items, testId }: { items: string[]; testId?: string }) {
  return (
    <ul className="flex flex-col gap-2" {...(testId ? { 'data-testid': testId } : {})}>
      {items.map((item) => (
        <li
          key={item}
          className="border-l-2 border-primary/30 pl-3 text-sm italic leading-relaxed text-muted-foreground"
        >
          {item}
        </li>
      ))}
    </ul>
  );
}
